import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { Form } from "@/components/form/form";
import { FormSelect, FormTextField } from "@/components/form/fields";
import { useToast } from "@/components/feedback/toaster";
import { AiDraftDialog } from "@/components/pipeline-canvas/ai-draft-dialog";
import {
  PIPELINE_TEMPLATES,
  type PipelineTemplate,
  templateSteps,
} from "@/components/pipeline-canvas/pipeline-templates";
import { apiRequest } from "@/lib/http";
import { useAiFeature, useAiStatus } from "@/api/ai";
import { useConnectors } from "@/api/connectors";
import { usePipelineMutations } from "@/api/pipelines";

const schema = z.object({
  name: z.string().min(2, "At least 2 characters"),
  description: z.string().optional(),
  executionMode: z.enum(["BATCH", "REALTIME", "MICRO_BATCH"]),
  sourceConnectorId: z.string().optional(),
  destConnectorId: z.string().optional(),
});
type Values = z.infer<typeof schema>;

export const Route = createFileRoute("/_app/pipelines/new")({
  component: NewPipelinePage,
});

function NewPipelinePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const connectors = useConnectors();
  const ai = useAiStatus();
  const draftEnabled = useAiFeature("draft");
  const { create } = usePipelineMutations();

  const [template, setTemplate] = useState<PipelineTemplate | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "", executionMode: "BATCH" },
  });

  const connectorOptions = [
    { value: "", label: "— none —" },
    ...(connectors.data ?? []).map((c) => ({ value: c.id, label: `${c.name} (${c.type})` })),
  ];

  function pickTemplate(t: PipelineTemplate) {
    setTemplate(t);
    form.reset({
      name: t.id === "blank" ? "" : t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      description: t.id === "blank" ? "" : t.blurb,
      executionMode: t.executionMode,
      sourceConnectorId: "",
      destConnectorId: "",
    });
  }

  async function onSubmit(values: Values) {
    const created = await create.mutateAsync({
      name: values.name,
      description: values.description || undefined,
      executionMode: values.executionMode,
      sourceConnectorId: values.sourceConnectorId || undefined,
      destConnectorId: values.destConnectorId || undefined,
    });
    if (template && template.steps.length > 0) {
      await apiRequest(`/pipelines/${created.id}/steps`, {
        method: "PUT",
        body: templateSteps(template),
      }).catch(() => {
        toast.error("Pipeline created, but seeding template steps failed");
      });
    }
    toast.success("Pipeline created");
    navigate({ to: "/pipelines/$pipelineId/edit", params: { pipelineId: created.id } });
  }

  return (
    <PageContainer maxWidth={720}>
      <PageHeader
        title="New pipeline"
        breadcrumbs={
          <Breadcrumbs items={[{ label: "Pipelines", to: "/pipelines" }, { label: "New" }]} />
        }
      />

      {!template ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick a starting point.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            {draftEnabled ? (
              <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
                <CardActionArea sx={{ p: 2, height: "100%" }} onClick={() => setAiOpen(true)}>
                  <Stack spacing={0.5}>
                    <AutoAwesomeIcon color="primary" />
                    <Typography variant="subtitle2">Draft with AI</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Describe the pipeline in plain English — {ai.data?.provider}
                    </Typography>
                  </Stack>
                </CardActionArea>
              </Card>
            ) : null}
            {PIPELINE_TEMPLATES.map((t) => (
              <Card key={t.id} variant="outlined">
                <CardActionArea sx={{ p: 2, height: "100%" }} onClick={() => pickTemplate(t)}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Typography variant="subtitle2">{t.name}</Typography>
                      {t.steps.length > 0 ? (
                        <Chip size="small" variant="outlined" label={`${t.steps.length} steps`} />
                      ) : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t.blurb}
                    </Typography>
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Stack>
      ) : (
        <Card sx={{ p: 3 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
            onClick={() => setTemplate(null)}
          >
            Change starting point
          </Button>
          <Form form={form} onSubmit={onSubmit}>
            <Stack spacing={2.5}>
              <FormTextField control={form.control} name="name" label="Name" required />
              <FormTextField
                control={form.control}
                name="description"
                label="Description"
                multiline
              />
              <FormSelect
                control={form.control}
                name="executionMode"
                label="Execution mode"
                options={[
                  { value: "BATCH", label: "Batch" },
                  { value: "REALTIME", label: "Realtime" },
                  { value: "MICRO_BATCH", label: "Micro-batch" },
                ]}
              />
              <FormSelect
                control={form.control}
                name="sourceConnectorId"
                label="Source connector"
                options={connectorOptions}
              />
              <FormSelect
                control={form.control}
                name="destConnectorId"
                label="Destination connector"
                options={connectorOptions}
              />
              {template.steps.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {template.steps.length} template step
                  {template.steps.length === 1 ? "" : "s"} will be added — refine them in the
                  canvas.
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" disabled={form.formState.isSubmitting}>
                  Create &amp; edit
                </Button>
                <Button onClick={() => navigate({ to: "/pipelines" })}>Cancel</Button>
              </Stack>
            </Stack>
          </Form>
        </Card>
      )}

      <AiDraftDialog open={aiOpen} onClose={() => setAiOpen(false)} />
    </PageContainer>
  );
}
